import { useEffect, useState } from 'react';
import { GlobeAltIcon } from '@heroicons/react/24/outline';
import { trackEvent, EventTypes } from '../utils/analytics.js';
import { userSession } from '../utils/userSession.js';

function useLogoTone(src) {
  const [tone, setTone] = useState('unknown');

  useEffect(() => {
    if (!src) {
      setTone('unknown');
      return;
    }

    let cancelled = false;
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      try {
        const size = 64;
        const canvas = document.createElement('canvas');
        canvas.width = size;
        canvas.height = size;
        const ctx = canvas.getContext('2d', { willReadFrequently: true });
        ctx.drawImage(img, 0, 0, size, size);
        const { data } = ctx.getImageData(0, 0, size, size);

        let sum = 0;
        let count = 0;
        for (let i = 0; i < data.length; i += 4) {
          if (data[i + 3] < 32) continue;
          sum += 0.2126 * data[i] + 0.7152 * data[i + 1] + 0.0722 * data[i + 2];
          count++;
        }

        if (!cancelled) {
          if (count === 0) setTone('unknown');
          else setTone(sum / count >= 160 ? 'light' : 'dark');
        }
      } catch {
        if (!cancelled) setTone('unknown');
      }
    };
    img.onerror = () => {
      if (!cancelled) setTone('unknown');
    };
    img.src = src;

    return () => {
      cancelled = true;
    };
  }, [src]);

  return tone;
}

function PartnerLogo({ src, alt }) {
  const tone = useLogoTone(src);
  const tileClass =
    tone === 'light'
      ? 'bg-slate-800'
      : tone === 'dark'
        ? 'bg-gray-100 ring-1 ring-gray-200'
        : 'bg-slate-400';

  return (
    <div className={`w-24 h-16 shrink-0 rounded-md flex items-center justify-center p-1.5 ${tileClass}`}>
      <img src={src} alt={alt} className="max-h-full max-w-full object-contain" />
    </div>
  );
}

function displayUrl(url) {
  if (!url) return '';
  try {
    const parsed = new URL(url);
    const path = parsed.pathname === '/' ? '' : parsed.pathname.replace(/\/$/, '');
    return `${parsed.host}${path}${parsed.search}${parsed.hash}`;
  } catch {
    return url;
  }
}

export default function PartnerCard({ partner, hoverScale = true, className = '', openOnClick = true }) {
  const handlePartnerClick = (clickTarget = 'card') => {
    trackEvent(EventTypes.PARTNER_CLICK, {
      partner_id: partner.PartnerID,
      partner_name: partner.BusinessName,
      click_target: clickTarget,
      user_id: userSession.getUserId() || 'anonymous'
    });
    window.open(partner.PartnerWebsiteLink, '_blank', 'noopener,noreferrer');
  };

  return (
    <div
      onClick={() => {
        if (!openOnClick) return;
        if (partner.PartnerWebsiteLink) handlePartnerClick('card');
      }}
      className={[
        'rounded-xl shadow-lg overflow-hidden flex flex-col bg-white',
        partner.PartnerWebsiteLink ? 'cursor-pointer' : '',
        hoverScale ? 'transform transition duration-200 hover:scale-105 hover:shadow-xl' : '',
        className
      ].join(' ')}
    >
      <div className="w-full aspect-[6/1] overflow-hidden bg-gradient-to-br from-slate-500 to-slate-800">
        {partner.PartnerObjImage && (
          <img
            src={partner.PartnerObjImage}
            alt={partner.PartnerName}
            className="block w-full h-full object-cover"
          />
        )}
      </div>

      <div className="bg-white px-4 py-3 flex items-center gap-4">
        {partner.PartnerLogo && (
          <PartnerLogo src={partner.PartnerLogo} alt={`${partner.PartnerName} logo`} />
        )}
        <div className="flex-1 min-w-0">
          <h3
            className="text-base font-semibold leading-snug text-gray-900 line-clamp-2 min-h-[2.5rem]"
            title={partner.PartnerName}
          >
            {partner.PartnerName}
          </h3>
          {partner.PartnerWebsiteLink && (
            <a
              href={partner.PartnerWebsiteLink}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => {
                if (!openOnClick) {
                  e.preventDefault();
                  return;
                }
                e.stopPropagation();
                handlePartnerClick('website_link');
              }}
              className="mt-2 inline-flex items-center min-w-0 max-w-full text-sm text-gray-500 hover:text-gray-700"
              title={partner.PartnerWebsiteLink}
            >
              <GlobeAltIcon className="h-4 w-4 mr-1 shrink-0" />
              <span className="truncate">{displayUrl(partner.PartnerWebsiteLink)}</span>
            </a>
          )}
        </div>
      </div>
    </div>
  );
}
